import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { ICellRendererParams, IRowNode } from '@ag-grid-community/core';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';

import { PurchaseOrderWorkflowDirective } from '@features/feature-workflow/directives/purchase-order-workflow.directive';
import { VendorWorkflowDirective } from '@features/feature-workflow/directives/vendor-workflow.directive';

import { IconComponent } from '@shared/components/icon/icon.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { TooltipDirective } from '@shared/directives/tooltip.directive';

type VendorGridActionParams = ICellRendererParams & {
  deleteClickFN: ((params: { rowNode: IRowNode }) => void) | undefined;
  editClickFN: ((params: { rowNode: IRowNode }) => void) | undefined;
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [VendorWorkflowDirective, PurchaseOrderWorkflowDirective],
  imports: [NgClass, TooltipDirective, IconComponent],
  selector: 'aux-vendor-grid-action',
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  template: `
    <div class="flex items-center justify-center space-x-2">
      @if (ctx.userHasVendorPermission()) {
        <button
          class="flex h-8 w-8 items-center justify-center p-0 text-aux-blue"
          type="button"
          (click)="onEditClick()"
        >
          <aux-icon name="Pencil" />
        </button>
      }
      @let errors = tooltipErrors();
      <button
        class="flex h-8 w-8 items-center justify-center p-0"
        [ngClass]="{ 'cursor-not-allowed opacity-50': errors.length }"
        type="button"
        auxTooltip
        [template]="errors.length > 1 ? errorArrayTooltip : undefined"
        [auxTooltip]="errors.length === 1 ? errors[0] : ''"
        [disabled]="errors.length"
        (click)="onDeleteClick()"
      >
        <aux-icon name="Trash" class="text-aux-error"></aux-icon>
      </button>

      <ng-template #errorArrayTooltip>
        <div class="rounded-[4px] py-2 pl-6 pr-4 text-white">
          <p>Vendor Contract cannot be deleted:</p>
          <ul class="list-disc">
            @for (err of errors; track err) {
              <li>{{ err }}</li>
            }
          </ul>
        </div>
      </ng-template>
    </div>
  `,
})
export class VendorGridActionComponent implements ICellRendererAngularComp {
  ctx = inject(VendorWorkflowDirective);
  purchaseOrderCtx = inject(PurchaseOrderWorkflowDirective);

  tooltipErrors = computed(() => {
    const errors: string[] = [];

    if (this.params.data.hasPurchaseOrders) {
      errors.push('Purchase Orders are linked.');
    }

    if (this.params.data.hasInvoices) {
      errors.push('Invoices are linked.');
    }

    if (!this.ctx.userHasVendorPermission()) {
      errors.push(MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION);
    }

    if (this.purchaseOrderCtx.isChangeOrdersWorkflowLocked()) {
      errors.push(MessagesConstants.LOCKED_FOR_PERIOD_CLOSE);
    }

    if (
      errors.length === 1 &&
      ['Invoices are linked.', 'Purchase Orders are linked.'].includes(errors[0])
    ) {
      return [`Cannot delete. ${errors[0]}`];
    }

    return errors;
  });

  params!: VendorGridActionParams;

  agInit(params: VendorGridActionParams): void {
    this.params = params;
  }

  onDeleteClick() {
    if (this.params.deleteClickFN) {
      this.params.deleteClickFN({ rowNode: this.params.node });
    }
  }

  onEditClick() {
    if (this.params.editClickFN) {
      this.params.editClickFN({ rowNode: this.params.node });
    }
  }

  refresh(): boolean {
    return false;
  }
}
